import { KeyValuePipe, NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbToastrService,
  NbToggleModule,
  NbTooltipModule
} from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { Xliff } from 'src/app/models/drb-models/xliff';
import { ACTIONS, BUNDLE_NAMES, PREFIX } from 'src/app/shared/constants/general-constants';
import { NgxConfirmModalComponent } from 'src/app/shared/ui/ngx-confirm-modal/ngx-confirm-modal.component';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { translationLanguages } from '../../../../shared/constants/translation-languages';
import { DrbState, DrbStateModel, SaveXliffState } from '../../redux';
import { DrbService } from '../../services/drb.service';
import { StringTranslationComponent } from './string-translation/string-translation.component';

@Component({
  selector: 'app-edit-xliff',
  templateUrl: './edit-xliff.component.html',
  styleUrls: ['./edit-xliff.component.css'],
  imports: [
    NbCardModule,
    NgIf,
    NbIconModule,
    NbButtonModule,
    FormsModule,
    NbInputModule,
    NbToggleModule,
    NbTooltipModule,
    NgFor,
    StringTranslationComponent,
    KeyValuePipe
  ]
})
export class EditXliffComponent implements OnInit, OnDestroy {
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public action;
  public xliff: Xliff;
  public xliffState;
  public username = '';
  public ids;
  public patternId = '^[a-z0-9_]+$';
  public patternFirstChar = '^\\S.*';
  public translationLanguages = translationLanguages;
  public initialSource;
  public bundle_name: string;
  public editable = false;

  constructor(
    protected ref: NbDialogRef<EditXliffComponent>,
    private store: Store,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private drbService: DrbService,
    private router: Router
  ) {}

  public ngOnInit() {
    this.bundle_name = this.router.url.split('/')[2];
    const drb = this.store.selectSnapshot(DrbState.getByKey(this.bundle_name as keyof DrbStateModel));
    this.xliffState = drb.xliff.en;
    const allDrbIds = this.drbService.getDrbIds();
    this.ids = allDrbIds.string
      .concat(allDrbIds.file)
      .concat(allDrbIds.image)
      .concat(allDrbIds.link)
      .concat(allDrbIds.video);
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.initialSource = this.xliff.source;
    if (this.action === ACTIONS.CREATE) {
      this.xliff.note = 'Do not translate';
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public save() {
    const localSourceXliff = JSON.parse(JSON.stringify(this.xliffState));

    if (this.action === ACTIONS.CREATE) {
      const xliffId = this.prefix() + this.xliff.id + '_dmc';
      if (this.ids.includes(xliffId)) {
        this.toastrService.show('', $localize`${xliffId} already exists!`, { status: 'danger' });
      } else {
        this.xliff.id = xliffId;
        this.xliff.version = 1;
        this.xliff.created_by = this.username;
        this.xliff.created_ts = Math.floor(Date.now() / 1000);
        this.xliff.last_updated_by = this.username;
        this.xliff.last_updated_ts = Math.floor(Date.now() / 1000);

        localSourceXliff.push(this.xliff);
        this.store.dispatch(new SaveXliffState(localSourceXliff, this.bundle_name));
        this.ref.close();
      }
    } else if (this.action === ACTIONS.EDIT) {
      const index = localSourceXliff.findIndex((item) => item.id === this.xliff.id);
      localSourceXliff[index].version = this.xliff.version + 1;
      localSourceXliff[index].source = this.xliff.source;
      localSourceXliff[index].note = this.editable ? 'Do not translate' : this.xliff.note;
      localSourceXliff[index].last_updated_by = this.username;
      localSourceXliff[index].last_updated_ts = Math.floor(Date.now() / 1000);

      if (this.initialSource !== this.xliff.source) {
        for (const lang in translationLanguages) {
          localSourceXliff[index][lang] = '';
        }
        this.confirmEditSave(localSourceXliff);
      } else if (this.initialSource === this.xliff.source) {
        for (const lang in translationLanguages) {
          localSourceXliff[index][lang] = this.xliff[lang];
        }
        this.store.dispatch(new SaveXliffState(localSourceXliff, this.bundle_name));
        this.ref.close();
      }
    }
  }

  public close() {
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Confirm Close`,
          message: $localize`Are you sure that you want to close the window? Any changes that you have made will be lost.`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
  }

  public confirmEditSave(localSourceXliff: Xliff) {
    this.dialogService
      .open(NgxConfirmModalComponent, {
        context: {
          title: $localize`Confirm Save`,
          yesButton: $localize`Save`,
          noButton: $localize`Cancel`,
          message:
            $localize`Before saving please be aware that editing the source field will clear all the current translations. Are you sure that you want to save your changes?` +
            '\n\n' +
            $localize`To save, type CONFIRM below and click SAVE`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.store.dispatch(new SaveXliffState(localSourceXliff, this.bundle_name));
          this.ref.close();
        }
      });
  }

  public descOrder = (a, b) => {
    if (a.key < b.key) {
      return b.key;
    }
  };

  public prefix() {
    if (this.bundle_name === BUNDLE_NAMES.MARKETING) {
      return PREFIX.MKT;
    } else if (this.bundle_name === BUNDLE_NAMES.MARKETING_TRANSLATIONS) {
      return PREFIX.MKT_T;
    }
  }
}
